import React, { ReactNode } from 'react';
import { isPanelCountryByName } from '../surveyConstants';
import { marketLabels } from './surveyMetrics';

export type MarketStats = Record<string, { completes: number; hits: number; irPct: number }>;

export const STATUS_DOT_COLORS: Record<string, string> = {
  Test: '#6c757d', Open: '#28a745', Hold: '#f0ad00', Wait: '#17a2b8', Close: '#343a40', Error: '#dc3545',
};
export const dotColor = (surveyType?: string): string => STATUS_DOT_COLORS[surveyType || ''] || '#adb5bd';

type Props = {
  markets: any[];
  selectedId: string;
  onSelect: (id: string) => void;
  // Per child survey id; shown as a muted line under a market when given.
  stats?: MarketStats;
  // Optional per-row action, rendered after the row. The action owns its click and
  // MUST call e.stopPropagation() so a click on it never also fires onSelect.
  // (It is a sibling of the row button, not nested in it: a button in a button is
  // invalid HTML.) Panel Launch does not pass it.
  renderRowAction?: (market: any) => ReactNode;
};

// The market rows shared by the Markets and Panel Launch panes. The caller owns
// the surrounding .dx-market-list card, heading and any buttons.
export const MarketList: React.FC<Props> = ({ markets, selectedId, onSelect, stats = {}, renderRowAction }) => {
  const labels = marketLabels(markets);
  return (
    <>
      {markets.length === 0 && <div className="text-muted small">No markets yet.</div>}
      {markets.map((m) => {
        const s = stats[m.id];
        const row = (
          <button
            key={m.id}
            type="button"
            className={`dx-market-item${String(m.id) === String(selectedId) ? ' active' : ''}`}
            onClick={() => onSelect(m.id)}
          >
            <span className="dx-status-dot" style={{ background: dotColor(m.surveyType) }} title={m.surveyType || ''} />
            <span className="dx-market-text">
              <strong>{labels[m.id]}</strong> {m.country}
              {m.draft && <span className="d-block text-muted small">unsaved</span>}
              {s && <span className="d-block text-muted small">{s.completes} completes · {s.hits} hits</span>}
            </span>
            {isPanelCountryByName(m.country) && <span className="badge badge-primary ml-auto">panel</span>}
          </button>
        );
        if (!renderRowAction) return row;
        return (
          <div key={m.id} className="dx-market-row">
            {row}
            <div className="dx-market-row-action">{renderRowAction(m)}</div>
          </div>
        );
      })}
    </>
  );
};
